import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { buildWorkspacePath, interpretServerAction, pushRecord, setActionRecord } from "../../core";
import type { Command, PlatformCommandPayloads, RunButtonPayload } from "../../core";
import { useRuntime } from "./RuntimeContext";
import { useWorkspaceNavigation } from "../navigation/useWorkspaceNavigation";
import { useWorkspace } from "../providers/WorkspaceProvider";
import { findActionInBoot } from "../../managers/workspace-utils";
import { useLocale } from "../providers/LocaleProvider";
import { useEvent } from "../hooks/useEvent";
import { toast } from "../hooks/use-toast";
import { useDialogActionStack } from "../../managers/DialogActionStack";

// Registers the platform command handlers on the central CommandManager. Handlers
// capture live navigation/locale/services via a ref, so a single registration
// stays correct as the route changes.
export function usePlatformCommands(): void {
    const runtime = useRuntime();
    const { orgCode, stack, navigateTo } = useWorkspaceNavigation();
    const locale = useLocale();
    const queryClient = useQueryClient();

    const dialogStack = useDialogActionStack();
    const boot = useWorkspace();

    const live = useRef({ orgCode, stack, navigateTo, locale, queryClient, dialogStack, boot });
    live.current = { orgCode, stack, navigateTo, locale, queryClient, dialogStack, boot };

    useEffect(() => {
        const bus = runtime.commandBus;
        const payloadOf = <K extends keyof PlatformCommandPayloads>(c: Command) => c.payload as PlatformCommandPayloads[K];

        const offs = [
            bus.register("NAVIGATE", (c) => live.current.navigateTo(payloadOf<"NAVIGATE">(c).to)),

            bus.register("OPEN_ACTION", (c) => {
                const p = payloadOf<"OPEN_ACTION">(c);
                // Resolve the target action once from boot so this single place
                // handles every call site (menus, command palette, app switcher,
                // server actions):
                //   • client action  → no view-mode segment (clean URL)
                //   • window action  → caller's explicit viewMode, else the
                //     action's OWN default view (e.g. kanban for the opportunity
                //     pipeline), and only then "list". A call site that omits
                //     viewMode (the app switcher) must not force list.
                const def = findActionInBoot(live.current.boot, p.actionKey);
                const viewMode = def?.clientComponent ? undefined : (p.viewMode ?? def?.defaultView ?? "list");
                live.current.navigateTo(
                    buildWorkspacePath(live.current.orgCode, [
                        { kind: "action", actionKey: p.actionKey, viewMode },
                    ]),
                );
            }),

            bus.register("OPEN_ACTION_DIALOG", (c) => {
                const p = payloadOf<"OPEN_ACTION_DIALOG">(c);
                live.current.dialogStack.push({
                    actionSlug: p.actionKey,
                    recordRef: p.recordRef,
                    viewMode: p.viewMode ?? "form",
                });
            }),

            bus.register("OPEN_RECORD", (c) => {
                const p = payloadOf<"OPEN_RECORD">(c);
                const stack = live.current.stack;
                const top = stack[stack.length - 1];
                let next;
                if (stack.length > 1 && top.kind === "list") {
                    // Top frame is a list drill (Enh 06). Opening a row sets
                    // THAT frame's recordRef in place — the breadcrumb chain
                    // (parent → drilled list → record) survives the click.
                    // The dispatcher's modelKey hint is ignored here; the
                    // drill frame already names its own action.
                    const updated = { ...top, recordRef: p.recordRef };
                    // Keep stack[0] (the required ActionFrame) explicit so the
                    // result still satisfies NavigationStack's tuple shape
                    // ([ActionFrame, ...DrillFrame[]]) — slicing alone widens to
                    // a plain array and the cast loses position 0.
                    next = [stack[0], ...stack.slice(1, -1), updated] as typeof stack;
                } else if (p.modelKey) {
                    // M2O drill (Mode A) — push an action-less record frame.
                    next = pushRecord(stack, { kind: "record", modelKey: p.modelKey, recordRef: p.recordRef, viewMode: "form" });
                } else {
                    // Plain "open a row of the root action" — set the root's recordRef.
                    next = setActionRecord(stack, p.recordRef);
                }
                live.current.navigateTo(buildWorkspacePath(live.current.orgCode, next));
            }),

            bus.register("CREATE_RECORD", () => {
                live.current.navigateTo(buildWorkspacePath(live.current.orgCode, setActionRecord(live.current.stack, "new")));
            }),

            bus.register("SET_PREFERENCE", (c) => {
                const p = payloadOf<"SET_PREFERENCE">(c);
                if (p.key === "language" && typeof p.value === "string") live.current.locale.setLanguage(p.value);
                if (p.key === "timezone" && typeof p.value === "string") live.current.locale.setTimezone(p.value);
            }),

            bus.register("SWITCH_ORG", async (c) => {
                const p = payloadOf<"SWITCH_ORG">(c);
                await runtime.services.auth.switchOrganization(p.organizationId);
                // Hard re-entry so the new token re-bootstraps cleanly (refined in Phase 2).
                if (typeof window !== "undefined") window.location.assign("/wc");
            }),

            // ── RUN_BUTTON — the central button dispatcher (WS 4.5) ──────────
            // Routes through the engine matching `commandType` (with default
            // fallback), normalises the response via `interpretServerAction`,
            // and dispatches any follow-up command (drill-down / external nav /
            // fresh action / etc.) back through the same bus. The bus-level
            // `pendingTrack` middleware tracks the spinner state — handlers do
            // NOT touch it.
            bus.register("RUN_BUTTON", async (c) => {
                const p = payloadOf<"RUN_BUTTON">(c) as RunButtonPayload;
                const { engine } = runtime.commandEngines.resolve(p, {
                    services: runtime.services,
                    orgCode: live.current.orgCode,
                });
                const result = await engine.dispatch(p, {
                    services: runtime.services,
                    orgCode: live.current.orgCode,
                });
                const follow = interpretServerAction(result, {
                    currentSlug: live.current.stack[0]?.actionKey ?? "",
                    currentStack: live.current.stack,
                    orgCode: live.current.orgCode,
                });
                // Success notification — fires on every explicit RUN_BUTTON
                // success. User-clicked buttons are workflow-style actions
                // from the user's perspective regardless of whether they
                // route through the workflow engine specifically. Variant
                // routing:
                //   - response.pending === true   → "warning" (approval queued)
                //   - otherwise                    → "success" (action committed)
                // Description carries the navigation target when one was
                // emitted so the user sees confirmation context, not just
                // an opaque "done".
                const labelText = (p.label && p.label.trim()) || p.commandName;
                const isApprovalPending =
                    result !== null
                    && typeof result === "object"
                    && (result as { pending?: boolean }).pending === true;
                // Handler-attached `warning` payload — `{ title?, message? }`
                // or a bare `string`. Surfaces as a warning toast instead of
                // the success toast. Lets handlers return a soft failure
                // (validation note, idempotent no-op, partial success) that
                // still completed in the engine sense.
                const warningPayload = (result !== null && typeof result === "object")
                    ? (result as { warning?: unknown }).warning
                    : undefined;
                const warning = typeof warningPayload === "string"
                    ? { message: warningPayload }
                    : (warningPayload && typeof warningPayload === "object")
                        ? warningPayload as { title?: string; message?: string }
                        : null;
                // Suppress the success toast for action-engine dispatches:
                // commandType="action" buttons just open a UI (route nav or
                // dialog) — the opening itself IS the user feedback. A toast
                // on top reads as noise ("3D View done · Opening the
                // target." over an opening dialog tells the user nothing
                // they don't already see). Workflow / approval / report /
                // default-engine dispatches still toast as before because
                // those return data-changing results the user can't see
                // from the dispatch alone.
                const isActionEngineDispatch = p.commandType === "action";

                if (isApprovalPending) {
                    toast({
                        variant: "warning",
                        title: `${labelText} — approval requested`,
                        description: "Waiting for approver decision before the action commits.",
                    });
                } else if (warning) {
                    toast({
                        variant: "warning",
                        title: warning.title || labelText,
                        description: warning.message || undefined,
                    });
                } else if (!isActionEngineDispatch) {
                    const followNav = follow?.type;
                    let description: string | undefined;
                    if (followNav === "OPEN_RECORD") {
                        const modelKey = (follow?.payload as { modelKey?: string } | undefined)?.modelKey;
                        description = modelKey
                            ? `Opening the new ${modelKey} record.`
                            : "Opening the updated record.";
                    } else if (followNav === "NAVIGATE") {
                        description = "Opening the target.";
                    } else {
                        description = "The action completed successfully.";
                    }
                    toast({
                        variant: "success",
                        title: `${labelText} done`,
                        description,
                    });
                }
                if (follow) {
                    // Same bus — pending state for THIS dispatch already
                    // cleared by the `finally` in pendingTrack before the
                    // follow-up runs.
                    await bus.dispatch(follow);
                }
                // Invalidate ALL open record queries + the action's record
                // list. A button dispatch frequently cascades into related
                // records — e.g. `crm.inquiry.convert_to_lead` creates a Lead
                // AND mutates the source Inquiry; deleting a Lead via a
                // related-record FK on_delete=set_null cascades into the
                // Inquiry's `converted_lead_id`. We can't predict every
                // cascade client-side, so we broadly invalidate `["record"]`
                // and `["action-records"]` and let TanStack refetch any
                // currently-mounted record on the next render. (Stale-time
                // 30s on the QueryClient would otherwise hide the cascade
                // until the user reloads the page.)
                live.current.queryClient.invalidateQueries({ queryKey: ["record"] });
                live.current.queryClient.invalidateQueries({ queryKey: ["action-records"] });
                return result;
            }),

            // ── REFETCH_RECORD — cross-user reload from web.client.command.done
            // The NotificationCenter dispatches this when SSE reports another
            // user (or our own backend) committed against a record. Same broad
            // invalidation strategy as RUN_BUTTON: refetch every open record
            // + every list. The bus's pendingTrack middleware coalesces
            // double-invalidation, so this overlapping with RUN_BUTTON's own
            // post-dispatch invalidation is harmless.
            bus.register("REFETCH_RECORD", () => {
                live.current.queryClient.invalidateQueries({ queryKey: ["record"] });
                live.current.queryClient.invalidateQueries({ queryKey: ["action-records"] });
            }),
        ];

        return () => {
            for (const off of offs) off();
        };
    }, [runtime]);

    // SSE bridge — fan `web.client.command.done` (a cross-user mutation) into
    // a REFETCH_RECORD command. Keeping this inside usePlatformCommands rather
    // than a separate NotificationCenter component so we don't need a new
    // provider mount point — same dependency surface (runtime + queryClient).
    useEvent("web.client.command.done", (payload) => {
        const p = (payload ?? {}) as Record<string, unknown>;
        const recordUuid = typeof p.record_uuid === "string"
            ? p.record_uuid
            : typeof p.recordUuid === "string"
                ? p.recordUuid
                : null;
        if (!recordUuid) return;
        const modelKey = typeof p.model_key === "string" ? p.model_key : undefined;
        const byUser = typeof p.by_user === "string" ? p.by_user : undefined;
        void runtime.commandBus.dispatch({
            type: "REFETCH_RECORD",
            payload: { modelKey, recordUuid, byUser },
        });
    });
}
